import {
  useFloating,
  offset,
  flip,
  shift,
  autoUpdate,
} from '@floating-ui/vue'
import { onClickOutside } from '@vueuse/core'

import type { CSSProperties } from 'vue'
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import type { ApiToken } from './interfaces'

import { faEllipsisVertical } from '@fortawesome/pro-regular-svg-icons'

import { useApiTokensStore } from './store'

interface UseApiTokenActionsOptions {
  token: ComputedRef<ApiToken>
}

interface ApiTokenActionsReturn {
  isOpen: Ref<boolean>
  containerRef: Ref<HTMLElement | null>
  floatingRef: Ref<HTMLElement | null>
  floatingStyles: Ref<CSSProperties>
  triggerIcon: IconDefinition
  toggle: () => void
  openEdit: () => void
  openDelete: () => void
}

export const useApiTokenActions = ({ token }: UseApiTokenActionsOptions): ApiTokenActionsReturn => {
  const store = useApiTokensStore()

  const isOpen = ref(false)
  const containerRef = ref<HTMLElement | null>(null)
  const floatingRef = ref<HTMLElement | null>(null)

  const { floatingStyles } = useFloating(containerRef, floatingRef, {
    placement: 'bottom-end',
    strategy: 'fixed',
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(4),
      flip(),
      shift({ padding: 8 }),
    ],
  })

  const toggle = (): void => {
    isOpen.value = !isOpen.value
  }

  const openEdit = (): void => {
    isOpen.value = false
    store.setTokenToEdit(token.value)
  }

  const openDelete = (): void => {
    isOpen.value = false
    store.setTokenToDelete(token.value)
  }

  onClickOutside(containerRef, () => {
    isOpen.value = false
  })

  return {
    isOpen,
    containerRef,
    floatingRef,
    floatingStyles,
    triggerIcon: faEllipsisVertical,
    toggle,
    openEdit,
    openDelete,
  }
}
